import { create } from "zustand";
import {
  DownloadJobState,
  DownloadJobStatus,
  enqueueDownload,
} from "../api/dataPipeline";

export interface DownloadEntry {
  jobId: string;
  cin: string;
  keys: string[];
  requestKey: string;
  state: DownloadJobState;
  progress: number;
  fileUrl?: string;
  fileSize?: number;
  fileName?: string;
  error?: string;
  triggered: boolean;
  requestedAt: number;
}

export function makeRequestKey(cin: string, keys: string[]): string {
  const sorted = [...keys].sort();
  return `${cin}|${sorted.join("|")}`;
}

const TERMINAL_STATES: DownloadJobState[] = ["completed", "failed"];
const IN_FLIGHT_STATES: DownloadJobState[] = ["waiting", "active", "delayed"];

export type RequestResult =
  | { kind: "started"; entry: DownloadEntry }
  | { kind: "duplicate"; entry: DownloadEntry }
  | { kind: "replay"; entry: DownloadEntry }
  | { kind: "error"; error: string };

interface DownloadsStore {
  jobs: Record<string, DownloadEntry>;
  requestKeyIndex: Record<string, string>;
  requestDownload: (cin: string, keys: string[]) => Promise<RequestResult>;
  applyStatus: (jobId: string, status: Partial<DownloadJobStatus>) => void;
  markTriggered: (jobId: string) => void;
  clearJob: (jobId: string) => void;
  getJobsForCin: (cin: string) => DownloadEntry[];
}

export const useDownloadsStore = create<DownloadsStore>((set, get) => ({
  jobs: {},
  requestKeyIndex: {},

  requestDownload: async (cin, keys) => {
    if (keys.length === 0) {
      return { kind: "error", error: "No files selected." };
    }
    const requestKey = makeRequestKey(cin, keys);
    const { jobs, requestKeyIndex } = get();
    const existingId = requestKeyIndex[requestKey];
    if (existingId) {
      const existing = jobs[existingId];
      if (existing && IN_FLIGHT_STATES.includes(existing.state)) {
        return { kind: "duplicate", entry: existing };
      }
      if (existing && existing.state === "completed" && existing.fileUrl) {
        return { kind: "replay", entry: existing };
      }
    }
    try {
      const res = await enqueueDownload({ keys, cin });
      const entry: DownloadEntry = {
        jobId: res.jobId,
        cin,
        keys: [...keys],
        requestKey,
        state: "waiting",
        progress: 0,
        triggered: false,
        requestedAt: Date.now(),
      };
      set((s) => ({
        jobs: { ...s.jobs, [entry.jobId]: entry },
        requestKeyIndex: { ...s.requestKeyIndex, [requestKey]: entry.jobId },
      }));
      return { kind: "started", entry };
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to start download.";
      return { kind: "error", error: msg };
    }
  },

  applyStatus: (jobId, status) => {
    set((s) => {
      const existing = s.jobs[jobId];
      if (!existing) return s;
      const next: DownloadEntry = {
        ...existing,
        state: (status.state as DownloadJobState) ?? existing.state,
        progress: status.progress ?? existing.progress,
        fileUrl: status.returnvalue?.fileUrl ?? existing.fileUrl,
        fileSize: status.returnvalue?.fileSize ?? existing.fileSize,
        error: status.failedReason ?? existing.error,
      };
      return { jobs: { ...s.jobs, [jobId]: next } };
    });
  },

  markTriggered: (jobId) => {
    set((s) => {
      const existing = s.jobs[jobId];
      if (!existing) return s;
      return {
        jobs: { ...s.jobs, [jobId]: { ...existing, triggered: true } },
      };
    });
  },

  clearJob: (jobId) => {
    set((s) => {
      const existing = s.jobs[jobId];
      if (!existing) return s;
      const { [jobId]: _removed, ...rest } = s.jobs;
      const idx = { ...s.requestKeyIndex };
      if (idx[existing.requestKey] === jobId) delete idx[existing.requestKey];
      return { jobs: rest, requestKeyIndex: idx };
    });
  },

  getJobsForCin: (cin) =>
    Object.values(get().jobs).filter((j) => j.cin === cin),
}));

export function isJobInFlight(entry: DownloadEntry | undefined): boolean {
  return !!entry && IN_FLIGHT_STATES.includes(entry.state);
}

export function isJobTerminal(entry: DownloadEntry | undefined): boolean {
  return !!entry && TERMINAL_STATES.includes(entry.state);
}
